import {Button, Col, Descriptions, Popover, Row, Skeleton, Timeline, Tooltip} from "antd";
import React from "react";
import {
    ColoredLine,
    DateRangePicker,
    DescriptionContainer,
    LeftButton,
    PastTabContainer,
    TableContainer,
    TimelineCard,
    TimeLineDuration
} from "../courierDetailStyles";
import {ConditionalTooltip} from "components/_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {useStore} from "hooks/useStore";
import {
    allowChangeStatuses,
    courierStatusColors,
    courierStatuses,
    deliveryStatuses
} from "constants/constants";
import {CheckOutlined, EditOutlined, ExclamationCircleOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment, {locale} from "moment";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {EmptyMessage} from "components/_defaultComponents/EmptyMessage/EmptyMessage";
import JSONInput from "react-json-editor-ajrm";
import {calculateDuration, getConstant, isConstantExist} from "utils/helpers";
import Column from "antd/lib/table/Column";

export const HistoryTab = ({
    courierData,
    courierStatus,
    orderInfoVisible,
    setOrderInfoVisible,
    setDateRange,
    setCourierLogs,
    courierLogs,
    dateRange,
    courierId,
    setVisibleStatusModal,
    ProductTable,
    isMobile
}) => {
    const stores = useStore();
    const {t, i18n} = useTranslation(["courierDetail"]);

    return (
        <>
            {!courierData ? (
                <Skeleton paragraph={{rows: 10, width: "100%"}} active={true}></Skeleton>
            ) : (
                <PastTabContainer>
                    <Row
                        type="flex"
                        gutter={[0, 10]}
                        style={{justifyContent: "space-between", flexWrap: "wrap"}}
                    >
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <h3
                                data-testid="courierName"
                                style={{fontSize: isMobile ? "16px" : "20px"}}
                            >
                                {courierData?.fullname}
                            </h3>
                        </Col>
                        {stores.meStore.checkRole("updateCourierStatus") &&
                            stores.meStore.checkPermission("couriers.statusHistory") && (
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={12}
                                    style={{textAlign: isMobile ? "left" : "right"}}
                                >
                                    <ConditionalTooltip
                                        condition={!allowChangeStatuses.has(courierStatus)}
                                        text={t("updateCourierStatusWarning", {
                                            ns: "courierDetail"
                                        })}
                                        data-testid="curierStatusUpdateButton"
                                        child={
                                            <LeftButton
                                                type="primary"
                                                onClick={() => {
                                                    setVisibleStatusModal(true);
                                                }}
                                                disabled={!allowChangeStatuses.has(courierStatus)}
                                                icon={<EditOutlined />}
                                            >
                                                {t("actions.updateCourierStatus", {
                                                    ns: "courierDetail"
                                                })}
                                            </LeftButton>
                                        }
                                    />
                                </Col>
                            )}
                    </Row>
                    <Row gutter={[isMobile ? 10 : 20, isMobile ? 10 : 20]}>
                        <Col xs={24} sm={24} md={8} lg={8}>
                            <DateRangePicker
                                locale={locale}
                                onChange={dates => {
                                    const updatedDate = [...dateRange];
                                    updatedDate[0] = dates;
                                    setDateRange(updatedDate);
                                }}
                                format={"DD/MM/YYYY"}
                                placeholder={t("fields.startDate", {ns: "common"})}
                                data-testid="startDate"
                            />
                        </Col>
                        <Col xs={24} sm={24} md={8} lg={8}>
                            <DateRangePicker
                                locale={locale}
                                onChange={dates => {
                                    const updatedDate = [...dateRange];
                                    updatedDate[1] = dates;
                                    setDateRange(updatedDate);
                                }}
                                format={"DD/MM/YYYY"}
                                placeholder={t("fields.endDate", {ns: "common"})}
                                data-testid="endDate"
                            />
                        </Col>
                        <Col xs={24} sm={24} md={8} lg={8}>
                            <Button
                                icon={<CheckOutlined />}
                                type="primary"
                                data-testid="submitButton"
                                style={{width: isMobile ? "100%" : "50px"}}
                                onClick={async () => {
                                    let start;
                                    let end;
                                    if (dateRange) {
                                        [start, end] = dateRange;
                                    }
                                    const result = await stores.courierStore.getCourierStatusLogs({
                                        courierId,
                                        startDate: dateRange[0] ? new Date(start) : null,
                                        endDate: dateRange[1] ? new Date(end) : null
                                    });
                                    setCourierLogs(result);
                                }}
                            />
                        </Col>
                    </Row>
                    <Row gutter={[isMobile ? 10 : 20, isMobile ? 10 : 20]}>
                        <Col span={24}>
                            <div
                                style={{fontSize: isMobile ? "13px" : "14px", marginBottom: "8px"}}
                            >
                                {t("historySearchTitle", {ns: "courierDetail"})}
                            </div>
                        </Col>
                        {courierLogs !== null &&
                            (!courierLogs.length ? (
                                <Col span={24}>
                                    <EmptyMessage
                                        descriptionKey="noInfoToDisplay"
                                        ns="courierDetail"
                                    />
                                </Col>
                            ) : (
                                <Col span={24} style={{overflow: "visible"}}>
                                    <Timeline mode="left" data-testid="timeline">
                                        {courierLogs.map((log, cardIndex) =>
                                            log.status ? (
                                                <Timeline.Item
                                                    key={cardIndex}
                                                    color={courierStatusColors[log.status]}
                                                    label={
                                                        <TimelineCard
                                                            right={!isMobile ? "true" : undefined}
                                                            data-testid="TimelineCard"
                                                            style={
                                                                isMobile
                                                                    ? {
                                                                          width: "100%",
                                                                          maxWidth: "100%"
                                                                      }
                                                                    : {}
                                                            }
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    gap: isMobile ? "4px" : "6px"
                                                                }}
                                                            >
                                                                <span
                                                                    data-testid="timelineText"
                                                                    style={{
                                                                        fontSize: isMobile
                                                                            ? "11px"
                                                                            : "13px",
                                                                        wordBreak: "break-word"
                                                                    }}
                                                                >
                                                                    {moment(
                                                                        new Date(log.createdAt)
                                                                    ).format(
                                                                        isMobile
                                                                            ? "DD/MM/YY HH:mm"
                                                                            : "L LTS"
                                                                    )}{" "}
                                                                    {log?.gpsProvider && (
                                                                        <Popover
                                                                            content={
                                                                                <JSONInput
                                                                                    id="courierLogGPSProviderJSON"
                                                                                    placeholder={
                                                                                        log?.gpsProvider
                                                                                    }
                                                                                    theme="light_mitsuketa_tribute"
                                                                                    width="auto"
                                                                                    height="auto"
                                                                                    viewOnly={true}
                                                                                    confirmGood={
                                                                                        false
                                                                                    }
                                                                                />
                                                                            }
                                                                        >
                                                                            <ExclamationCircleOutlined
                                                                                style={{
                                                                                    fontSize: isMobile
                                                                                        ? "10px"
                                                                                        : "12px"
                                                                                }}
                                                                            />
                                                                        </Popover>
                                                                    )}
                                                                </span>
                                                                <TimeLineDuration
                                                                    data-testid="TimelineDuration"
                                                                    style={{
                                                                        fontWeight: "bold",
                                                                        fontSize: isMobile
                                                                            ? 11
                                                                            : 13,
                                                                        margin: 0,
                                                                        wordBreak: "break-word"
                                                                    }}
                                                                >
                                                                    {courierLogs[cardIndex - 1] &&
                                                                        t("duration", {
                                                                            ns: "courierDetail"
                                                                        }) +
                                                                            ": " +
                                                                            calculateDuration({
                                                                                startDate:
                                                                                    log.createdAt,
                                                                                endDate:
                                                                                    courierLogs[
                                                                                        cardIndex -
                                                                                            1
                                                                                    ].createdAt,
                                                                                dayUnit: t(
                                                                                    "units.day.short",
                                                                                    {
                                                                                        ns: "common"
                                                                                    }
                                                                                ),
                                                                                hourUnit: t(
                                                                                    "units.hour.short",
                                                                                    {
                                                                                        ns: "common"
                                                                                    }
                                                                                ),
                                                                                minuteUnit: t(
                                                                                    "units.minute.short",
                                                                                    {
                                                                                        ns: "common"
                                                                                    }
                                                                                ),
                                                                                secondUnit: t(
                                                                                    "units.second.short",
                                                                                    {
                                                                                        ns: "common"
                                                                                    }
                                                                                )
                                                                            })}
                                                                </TimeLineDuration>
                                                            </div>
                                                        </TimelineCard>
                                                    }
                                                >
                                                    <ColoredLine
                                                        data-testid="coloredLine"
                                                        color={courierStatusColors[log.status]}
                                                    />
                                                    <DescriptionContainer
                                                        color={courierStatusColors[log.status]}
                                                        bordered
                                                        column={1}
                                                        style={
                                                            isMobile
                                                                ? {width: "100%", maxWidth: "100%"}
                                                                : {}
                                                        }
                                                    >
                                                        <Descriptions.Item>
                                                            <Tooltip
                                                                title={
                                                                    isConstantExist(
                                                                        courierStatuses,
                                                                        log.status
                                                                    )
                                                                        ? t(
                                                                              getConstant(
                                                                                  courierStatuses,
                                                                                  log.status
                                                                              ).translation_key,
                                                                              {
                                                                                  ns:
                                                                                      "courierStatuses"
                                                                              }
                                                                          )
                                                                        : "-"
                                                                }
                                                            >
                                                                <span
                                                                    data-testid="courierStatuses"
                                                                    style={{cursor: "default"}}
                                                                >
                                                                    {isConstantExist(
                                                                        courierStatuses,
                                                                        log.status
                                                                    )
                                                                        ? t(
                                                                              getConstant(
                                                                                  courierStatuses,
                                                                                  log.status
                                                                              ).translation_key,
                                                                              {
                                                                                  ns:
                                                                                      "courierStatuses"
                                                                              }
                                                                          )
                                                                        : "-"}
                                                                </span>
                                                            </Tooltip>
                                                        </Descriptions.Item>
                                                        {log.reason && (
                                                            <Descriptions.Item
                                                                label={t("logReason", {
                                                                    ns: "courierDetail"
                                                                })}
                                                            >
                                                                <Tooltip title={log.reason || "-"}>
                                                                    <span
                                                                        data-testid="reason"
                                                                        style={{cursor: "default"}}
                                                                    >
                                                                        {log.reason || "-"}
                                                                    </span>
                                                                </Tooltip>
                                                            </Descriptions.Item>
                                                        )}
                                                        {log.details?.mood && (
                                                            <Descriptions.Item
                                                                label={t("logMood", {
                                                                    ns: "courierDetail"
                                                                })}
                                                            >
                                                                <Tooltip
                                                                    title={log.details.mood.toUpperCase()}
                                                                >
                                                                    <span
                                                                        data-testid="mood"
                                                                        style={{cursor: "default"}}
                                                                    >
                                                                        {log.details.mood.toUpperCase()}
                                                                    </span>
                                                                </Tooltip>
                                                            </Descriptions.Item>
                                                        )}
                                                        {log.details?.note && (
                                                            <Descriptions.Item
                                                                label={t("logDetail", {
                                                                    ns: "courierDetail"
                                                                })}
                                                            >
                                                                <Tooltip title={log.details.note}>
                                                                    <span
                                                                        data-testid="detailsNote"
                                                                        style={{cursor: "default"}}
                                                                    >
                                                                        {log.details.note}
                                                                    </span>
                                                                </Tooltip>
                                                            </Descriptions.Item>
                                                        )}
                                                    </DescriptionContainer>
                                                </Timeline.Item>
                                            ) : (
                                                <Timeline.Item
                                                    key={cardIndex}
                                                    label={
                                                        <TimelineCard
                                                            data-testid="timelineCard"
                                                            right={!isMobile ? "true" : undefined}
                                                            style={
                                                                isMobile
                                                                    ? {
                                                                          width: "100%",
                                                                          maxWidth: "100%"
                                                                      }
                                                                    : {}
                                                            }
                                                            title={
                                                                <span
                                                                    data-testid="createdAt"
                                                                    style={{
                                                                        fontSize: isMobile
                                                                            ? "11px"
                                                                            : "16px",
                                                                        wordBreak: "break-word",
                                                                        display: "block"
                                                                    }}
                                                                >
                                                                    {moment(new Date(log.createdAt))
                                                                        .locale(
                                                                            i18n.resolvedLanguage ||
                                                                                "tr"
                                                                        )
                                                                        .format(
                                                                            isMobile
                                                                                ? "DD/MM/YY HH:mm"
                                                                                : "ddd, MMM D YYYY LTS"
                                                                        )}
                                                                </span>
                                                            }
                                                        >
                                                            <Tooltip
                                                                title={log.company.companyName}
                                                            >
                                                                <span
                                                                    data-testid="companyName"
                                                                    style={{
                                                                        fontSize: isMobile
                                                                            ? "12px"
                                                                            : "14px",
                                                                        wordBreak: "break-word",
                                                                        display: "block",
                                                                        cursor: "default"
                                                                    }}
                                                                >
                                                                    {log.company.companyName}
                                                                </span>
                                                            </Tooltip>
                                                        </TimelineCard>
                                                    }
                                                >
                                                    <TimelineCard
                                                        style={
                                                            isMobile
                                                                ? {width: "100%", maxWidth: "100%"}
                                                                : {}
                                                        }
                                                        title={
                                                            <>
                                                                <Tooltip
                                                                    title={
                                                                        isConstantExist(
                                                                            deliveryStatuses,
                                                                            log.deliveryState
                                                                        )
                                                                            ? (() => {
                                                                                  const constant = getConstant(
                                                                                      deliveryStatuses,
                                                                                      log.deliveryState
                                                                                  );
                                                                                  if (
                                                                                      log.deliveryState ===
                                                                                          "returned" &&
                                                                                      log.orders
                                                                                          ?.length &&
                                                                                      log.orders?.every(
                                                                                          order =>
                                                                                              order?.state ===
                                                                                              "canceled"
                                                                                      )
                                                                                  ) {
                                                                                      return t(
                                                                                          `${constant.translation_key}.shortName`,
                                                                                          {
                                                                                              ns:
                                                                                                  "deliveryStatuses"
                                                                                          }
                                                                                      );
                                                                                  }
                                                                                  return t(
                                                                                      `${constant.translation_key}.name`,
                                                                                      {
                                                                                          ns:
                                                                                              "deliveryStatuses"
                                                                                      }
                                                                                  );
                                                                              })()
                                                                            : "-"
                                                                    }
                                                                >
                                                                    <span
                                                                        data-testid="deliveryStatuses"
                                                                        style={{
                                                                            fontSize: isMobile
                                                                                ? "12px"
                                                                                : "16px",
                                                                            wordBreak: "break-word",
                                                                            display: "block",
                                                                            cursor: "default"
                                                                        }}
                                                                    >
                                                                        {isConstantExist(
                                                                            deliveryStatuses,
                                                                            log.deliveryState
                                                                        )
                                                                            ? (() => {
                                                                                  const constant = getConstant(
                                                                                      deliveryStatuses,
                                                                                      log.deliveryState
                                                                                  );
                                                                                  if (
                                                                                      log.deliveryState ===
                                                                                          "returned" &&
                                                                                      log.orders
                                                                                          ?.length &&
                                                                                      log.orders?.every(
                                                                                          order =>
                                                                                              order?.state ===
                                                                                              "canceled"
                                                                                      )
                                                                                  ) {
                                                                                      return t(
                                                                                          `${constant.translation_key}.shortName`,
                                                                                          {
                                                                                              ns:
                                                                                                  "deliveryStatuses"
                                                                                          }
                                                                                      );
                                                                                  }
                                                                                  return t(
                                                                                      `${constant.translation_key}.name`,
                                                                                      {
                                                                                          ns:
                                                                                              "deliveryStatuses"
                                                                                      }
                                                                                  );
                                                                              })()
                                                                            : "-"}
                                                                    </span>
                                                                </Tooltip>
                                                                {log.deliveryState ===
                                                                    "canceled" && (
                                                                    <Tooltip
                                                                        title={`${t(
                                                                            "cancelReason",
                                                                            {ns: "courierDetail"}
                                                                        )}: ${log.cancelText}`}
                                                                    >
                                                                        <h6
                                                                            style={{
                                                                                fontSize: isMobile
                                                                                    ? "11px"
                                                                                    : "13px",
                                                                                wordBreak:
                                                                                    "break-word",
                                                                                margin: "4px 0",
                                                                                cursor: "default"
                                                                            }}
                                                                        >
                                                                            {t("cancelReason", {
                                                                                ns: "courierDetail"
                                                                            })}
                                                                            :{" "}
                                                                            <span data-testid="cancelText">
                                                                                {log.cancelText}
                                                                            </span>
                                                                        </h6>
                                                                    </Tooltip>
                                                                )}
                                                            </>
                                                        }
                                                    >
                                                        <Popover
                                                            content={
                                                                <>
                                                                    <TableContainer
                                                                        rowKey="_id"
                                                                        dataSource={log.orders}
                                                                        pagination={false}
                                                                        data-testid="ordersTable"
                                                                        scroll={{
                                                                            x: isMobile
                                                                                ? 400
                                                                                : undefined
                                                                        }}
                                                                        expandable={
                                                                            ProductTable
                                                                                ? {
                                                                                      expandedRowRender: record => {
                                                                                          return (
                                                                                              <ProductTable
                                                                                                  dataSource={
                                                                                                      record.products
                                                                                                  }
                                                                                              />
                                                                                          );
                                                                                      }
                                                                                  }
                                                                                : undefined
                                                                        }
                                                                    >
                                                                        <Column
                                                                            dataIndex="receiverName"
                                                                            title={t(
                                                                                "fields.customerName",
                                                                                {
                                                                                    ns: "common"
                                                                                }
                                                                            )}
                                                                            render={text => (
                                                                                <span data-testid="receiverName">
                                                                                    {text}
                                                                                </span>
                                                                            )}
                                                                        ></Column>
                                                                        <Column
                                                                            dataIndex="receiverPhone"
                                                                            title={t(
                                                                                "fields.customerPhone",
                                                                                {
                                                                                    ns:
                                                                                        "courierDetail"
                                                                                }
                                                                            )}
                                                                            render={text => (
                                                                                <span data-testid="receiverPhone">
                                                                                    {text}
                                                                                </span>
                                                                            )}
                                                                        ></Column>
                                                                        <Column
                                                                            dataIndex="receiverAddress"
                                                                            title={t(
                                                                                "fields.customerAddress",
                                                                                {
                                                                                    ns:
                                                                                        "courierDetail"
                                                                                }
                                                                            )}
                                                                            render={text => (
                                                                                <span data-testid="receiverAddress">
                                                                                    {text}
                                                                                </span>
                                                                            )}
                                                                        ></Column>
                                                                    </TableContainer>
                                                                </>
                                                            }
                                                            trigger="click"
                                                            visible={
                                                                orderInfoVisible[log._id] || false
                                                            }
                                                            onVisibleChange={visible => {
                                                                const abc = {};
                                                                abc[log._id] = visible;
                                                                setOrderInfoVisible(abc);
                                                            }}
                                                        >
                                                            <Tooltip
                                                                title={log.orders
                                                                    .map(order => order.orderNumber)
                                                                    .join(", ")}
                                                            >
                                                                <a
                                                                    style={{
                                                                        fontSize: isMobile
                                                                            ? "11px"
                                                                            : "13px",
                                                                        wordBreak: "break-all",
                                                                        display: "block",
                                                                        maxWidth: "100%",
                                                                        overflowWrap: "break-word",
                                                                        cursor: "pointer"
                                                                    }}
                                                                >
                                                                    {log.orders.map(
                                                                        (order, orderIndex) =>
                                                                            `${order.orderNumber}${
                                                                                log.orders[
                                                                                    orderIndex + 1
                                                                                ]
                                                                                    ? ", "
                                                                                    : ""
                                                                            }`
                                                                    )}
                                                                </a>
                                                            </Tooltip>
                                                        </Popover>
                                                    </TimelineCard>
                                                </Timeline.Item>
                                            )
                                        )}
                                    </Timeline>
                                </Col>
                            ))}
                    </Row>
                </PastTabContainer>
            )}
        </>
    );
};
